import Cookies from 'js-cookie';

/**
 * Helper function to detect if we're in admin portal context
 * @returns {boolean} - True if in admin portal, false otherwise
 */
const isAdminPortal = () => {
  return window.location.pathname.startsWith('/glam-admin/');
};

/**
 * Helper function to get the appropriate authentication token based on context
 * @returns {string|null} - The authentication token to use
 */
const getAuthToken = () => {
  if (isAdminPortal()) {
    // In admin portal, use admin token to access any merchant's data
    return Cookies.get('access_token');
  } else {
    // In merchant portal, use merchant token
    return Cookies.get('merchant_access_token');
  }
};

/**
 * Get the actual S3 URL for a generation by fetching it from the backend
 * This is the proper way to get secure, authenticated image URLs
 * 
 * @param {number} generationId - The generation ID
 * @param {string} type - Optional type (e.g., 'enhanced')
 * @returns {Promise<string>} - Promise that resolves to the S3 URL
 */
export const getS3ImageUrl = async (generationId, type = null) => {
  const { API_URLS } = require('../config/apiConfig');

  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Fetch the S3 URL from the backend
    const response = await fetch(`${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/preview/${generationId}${type ? `?type=${type}` : ''}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Parse the response to get the S3 URL
    const data = await response.json();

    if (data.success && data.s3_url) {
      // Return the S3 URL directly - this will load the image from S3
      return data.s3_url;
    } else {
      throw new Error('No URL found in response');
    }
  } catch (error) {
    console.error('Failed to get image URL:', error);
    // Fallback to backend endpoint
    return `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/preview/${generationId}${type ? `?type=${type}` : ''}`;
  }
};

/**
 * Get preview URL for a generation (returns backend endpoint)
 * 
 * @param {number} generationId - The generation ID
 * @param {string} type - Optional type (e.g., 'enhanced')
 * @returns {string} - Backend endpoint URL
 */
export const getSecurePreviewUrl = (generationId, type = null) => {
  const { API_URLS } = require('../config/apiConfig');

  let baseUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/preview/${generationId}`;

  if (type) {
    baseUrl += `?type=${type}`;
  }

  return baseUrl;
};

/**
 * Get thumbnail URL for a generation
 * 
 * @param {number} generationId - The generation ID
 * @param {string} size - Optional size (e.g., '400x400')
 * @returns {string} - Thumbnail endpoint URL
 */
export const getThumbnailUrl = (generationId, size = 'thumbnail', type = null) => {
  const { API_URLS } = require('../config/apiConfig');

  let url = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/thumbnail/${generationId}`;

  const params = [];
  if (size) params.push(`size=${size}`);
  if (type) params.push(`type=${type}`);

  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  return url;
};

/**
 * Merchant model catalog image URL — same shape as getThumbnailUrl (Gallery uses generation id here).
 *
 * @param {number} catalogImageId - model_catalogs.id
 * @param {string} size - default 'thumbnail' (backend maps to 400x400 like ai-generation)
 * @param {string|null} type - reserved; model-face endpoint ignores
 * @returns {string}
 */
export const getModelFaceThumbnailUrl = (catalogImageId, size = 'thumbnail', type = null) => {
  const { API_URLS } = require('../config/apiConfig');

  let url = `${API_URLS.BACKEND_BASE}/api/merchant/model-face/thumbnail/${catalogImageId}`;

  const params = [];
  if (size) params.push(`size=${size}`);
  if (type) params.push(`type=${type}`);

  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  return url;
};

/**
 * Get secure video stream URL for a generation (now uses HTTP headers for authentication)
 * 
 * @param {number} generationId - The generation ID
 * @returns {string} - URL without token (authentication via HTTP headers)
 */
export const getSecureVideoStreamUrl = (generationId) => {
  const { API_URLS } = require('../config/apiConfig');

  const baseUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/video-stream/${generationId}`;

  return baseUrl;
};

/**
 * Get secure image URL for a generation (now uses HTTP headers for authentication)
 * 
 * @param {number} generationId - The generation ID
 * @returns {string} - URL without token (authentication via HTTP headers)
 */
export const getSecureImageUrl = (generationId) => {
  const { API_URLS } = require('../config/apiConfig');

  const baseUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/preview/${generationId}`;

  return baseUrl;
};

/**
 * Create an authenticated image element that automatically handles JWT authentication
 * This is a drop-in replacement for <img> tags
 */
export const createAuthenticatedImage = (src, alt = '', options = {}) => {
  // Create a canvas element to draw the authenticated image
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Set canvas size
  if (options.width) canvas.width = options.width;
  if (options.height) canvas.height = options.height;

  // Get JWT token based on context
  const token = getAuthToken();

  if (!token) {
    console.error('No authentication token found');
    return canvas;
  }

  // Fetch image with authentication
  fetch(src, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.blob())
    .then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Trigger load event
        if (options.onLoad) {
          options.onLoad({ target: canvas });
        }
      };

      img.onerror = () => {
        // Trigger error event
        if (options.onError) {
          options.onError({ target: canvas });
        }
      };

      img.src = objectUrl;
    })
    .catch(error => {
      console.error('Failed to fetch authenticated image:', error);
      if (options.onError) {
        options.onError({ target: canvas });
      }
    });

  return canvas;
};
